import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api";
import type { Dayjs } from "dayjs";
import type { ID } from "../id";

export interface AuditLogEntry {
  id: ID;
  created_at: Dayjs;
  user_id?: ID;
  email?: string;
  display_name?: string;
  action: string;
  entity_type?: string;
  entity_id?: string;
  details?: string;
  ip?: string;
  impersonator_user_id?: ID;
  impersonator_email?: string;
  impersonator_display_name?: string;
}

export interface AuditLogListResponse {
  items: AuditLogEntry[];
  next_cursor: string | null;
  total_estimate: number;
}

export interface AuditLogFilters {
  from?: string;
  to?: string;
  user_id?: ID;
  entity_type?: string;
  q?: string;
  cursor?: string;
  limit?: number;
  /**
   * Drops whole action families by prefix, e.g. `["auth.", "impersonation."]`.
   * Applied server-side on purpose: a feed of eight rows cannot be filtered
   * after the fact — a handful of logins would leave it empty.
   */
  exclude_action_prefix?: string[];
}

export interface AuditLogUser {
  id: ID;
  email?: string;
  display_name: string;
}

interface AuditLogUsersResponse {
  users: AuditLogUser[];
}

// `options.enabled` matters: the endpoint is admin / members_admin only, so a
// surface that merely hides the card still fires a 401/403 for every other
// role. Gate the query, not the render.
export function useAuditLogs(filters: AuditLogFilters, options?: { enabled?: boolean }) {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.user_id) params.set("user_id", String(filters.user_id));
  if (filters.entity_type) params.set("entity_type", filters.entity_type);
  if (filters.q) params.set("q", filters.q);
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.limit) params.set("limit", String(filters.limit));
  for (const prefix of filters.exclude_action_prefix ?? []) {
    params.append("exclude_action_prefix", prefix);
  }

  const qs = params.toString();

  return useQuery<AuditLogListResponse>({
    queryKey: ["audit-logs", filters],
    queryFn: () => apiGet<AuditLogListResponse>(`/audit-logs${qs ? "?" + qs : ""}`),
    enabled: options?.enabled ?? true,
  });
}

export function useAuditLogUsers() {
  return useQuery<AuditLogUser[]>({
    queryKey: ["audit-log-users"],
    queryFn: async () => {
      const res = await apiGet<AuditLogUsersResponse>("/audit-logs/users");
      return res.users;
    },
    staleTime: 5 * 60 * 1000,
  });
}
